import DOMPurify from "dompurify";

/**
 * Render a *trusted-but-sanitized* subset of Markdown to HTML.
 *
 * Markdown features supported: headings (#, ##, ###), bold (**x**), italic (*x*),
 * links [text](url), unordered lists, and fenced ``` code blocks.
 *
 * The HTML is then run through DOMPurify with a strict allow-list so that even if
 * the source contains script tags or event handlers nothing dangerous reaches
 * the DOM.
 */
export function renderMarkdownSafe(md, theme = "public") {
  if (!md) return "";

  const esc = (s) =>
    s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

  let html = esc(md);

  // Code fences
  html = html.replace(
    /```([\s\S]*?)```/g,
    (_, c) =>
      `<pre class="bg-white/5 p-3 rounded-sm overflow-x-auto text-xs">${c}</pre>`
  );

  if (theme === "admin-preview") {
    html = html.replace(
      /^### (.*)$/gm,
      '<h3 style="font-size:1.1em;color:#fff;margin:1em 0 0.5em">$1</h3>'
    );
    html = html.replace(
      /^## (.*)$/gm,
      '<h2 style="font-size:1.3em;color:#C5A059;margin:1.5em 0 0.5em">$1</h2>'
    );
    html = html.replace(
      /^# (.*)$/gm,
      '<h1 style="font-size:1.7em;color:#fff;margin:1em 0 0.7em">$1</h1>'
    );
  } else {
    html = html.replace(
      /^### (.*)$/gm,
      '<h3 class="text-lg font-semibold mt-6 mb-2 text-white">$1</h3>'
    );
    html = html.replace(
      /^## (.*)$/gm,
      '<h2 class="text-xl font-semibold mt-8 mb-3 text-[#C5A059]">$1</h2>'
    );
    html = html.replace(
      /^# (.*)$/gm,
      '<h1 class="text-3xl font-bold mt-4 mb-6 text-white">$1</h1>'
    );
  }

  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/\*(.+?)\*/g, "<em>$1</em>");
  html = html.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" class="text-[#C5A059] underline" target="_blank" rel="noreferrer">$1</a>'
  );
  html = html.replace(
    /^\s*-\s(.+)$/gm,
    '<li class="ml-6 list-disc text-white/80">$1</li>'
  );

  html = html
    .split(/\n\n+/)
    .map((p) => {
      const trimmed = p.trim();
      if (!trimmed) return "";
      if (/^<(h[1-6]|li|pre|ul|ol)/i.test(trimmed)) return trimmed;
      if (theme === "admin-preview") {
        return `<p style="margin:0.7em 0">${trimmed.replace(/\n/g, "<br/>")}</p>`;
      }
      return `<p class="text-white/70 leading-relaxed mb-4">${trimmed.replace(/\n/g, "<br/>")}</p>`;
    })
    .join("\n");

  html = html.replace(
    /(<li[^>]*>.*?<\/li>(\s*<li[^>]*>.*?<\/li>)*)/gs,
    '<ul class="my-4 space-y-1">$1</ul>'
  );

  // Final defense-in-depth: strip any tag/attr that could execute JS.
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "p", "br", "strong", "em", "a", "h1", "h2", "h3", "h4", "h5", "h6",
      "ul", "ol", "li", "pre", "code", "blockquote", "hr", "span", "div",
    ],
    ALLOWED_ATTR: ["href", "class", "style", "target", "rel"],
    ALLOW_DATA_ATTR: false,
  });
}

/** Sanitize a pre-rendered HTML fragment (for non-markdown content like generated emails). */
export function sanitizeHtml(html) {
  if (!html) return "";
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "p", "br", "strong", "em", "a", "h1", "h2", "h3", "h4", "h5", "h6",
      "ul", "ol", "li", "pre", "code", "blockquote", "hr", "span", "div",
      "table", "thead", "tbody", "tr", "td", "th", "img",
    ],
    ALLOWED_ATTR: ["href", "class", "style", "target", "rel", "src", "alt", "width", "height", "colspan", "rowspan"],
    ALLOW_DATA_ATTR: false,
  });
}
